import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { 
  sectionVariant, 
  staggerContainer, 
  itemVariant,
  viewportOnce,
  viewportOnceNear
} from "@/lib/motion";
import { useIsMobile } from "@/hooks/use-mobile";

export interface EngagementPhase {
  phase: string;
  title: string;
  description: string;
}

interface EngagementShapeProps {
  title?: string;
  description?: string;
  phases?: EngagementPhase[];
}

const defaultPhases: EngagementPhase[] = [
  {
    phase: "Diagnose",
    title: "Understand current state",
    description: "Map constraints, gaps, and opportunities before action.",
  },
  {
    phase: "Design",
    title: "Define the correction",
    description: "Align structure to objectives. Systems, not shortcuts.",
  },
  {
    phase: "Execute",
    title: "Implement with precision",
    description: "Phased rollout with measurement at each stage.",
  },
  {
    phase: "Review",
    title: "Continuous refinement",
    description: "Data-informed iteration. Compound improvements.",
  },
];

const EngagementShape = ({ 
  title = "How engagement unfolds.",
  description,
  phases = defaultPhases,
}: EngagementShapeProps) => {
  const isMobile = useIsMobile();
  const [expandedPhase, setExpandedPhase] = useState<number | null>(null);

  const togglePhase = (index: number) => {
    setExpandedPhase(expandedPhase === index ? null : index);
  };

  return (
    <section className="py-10 md:py-12 lg:py-16 border-t border-border">
      <div className="container-wide">
        <motion.div
          className="max-w-3xl mb-8 md:mb-10"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={sectionVariant}
        >
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">
            Engagement Structure
          </span>
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-3">
            {title}
          </h2>
          {description && (
            <p className="text-muted-foreground leading-relaxed text-sm md:text-base">
              {description}
            </p>
          )}
        </motion.div>

        {/* Desktop: Grid layout */}
        {!isMobile && (
          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnceNear}
            variants={staggerContainer}
          >
            {phases.map((phase, index) => (
              <motion.div
                key={phase.phase}
                variants={itemVariant}
                className="bg-background p-6 md:p-8 relative group"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-medium text-primary/60 uppercase tracking-wider">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="flex-1 h-px bg-border group-hover:bg-primary/20 transition-colors" />
                </div>
                <h3 className="text-base font-display font-medium text-foreground mb-2">
                  {phase.phase}
                </h3>
                <p className="text-sm text-foreground/80 mb-2">
                  {phase.title}
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {phase.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Mobile: Accordion-style progressive disclosure */}
        {isMobile && (
          <motion.div
            className="space-y-0 divide-y divide-border border-y border-border"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnceNear}
            variants={staggerContainer}
          >
            {phases.map((phase, index) => (
              <motion.div
                key={phase.phase}
                variants={itemVariant}
                className="bg-background"
              >
                <button
                  onClick={() => togglePhase(index)}
                  className="w-full flex items-center justify-between py-4 px-1 text-left"
                  aria-expanded={expandedPhase === index}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-medium text-primary/60 uppercase tracking-wider">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="text-sm font-medium text-foreground">
                      {phase.phase}
                    </span>
                  </div>
                  <ChevronDown 
                    className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${
                      expandedPhase === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {expandedPhase === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      className="overflow-hidden"
                    >
                      <div className="pb-4 px-1 pl-10">
                        <p className="text-sm text-foreground/80 mb-1">
                          {phase.title}
                        </p>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {phase.description}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Minimal note with modularity and ownership signals */}
        <motion.div
          className="mt-6 max-w-xl space-y-2"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={viewportOnce}
          transition={{ delay: 0.4, duration: 0.3 }}
        >
          <p className="text-xs text-muted-foreground">
            No fixed timelines. No deliverables lists. Structure adapts to your constraints and objectives.
          </p>
          <p className="text-xs text-muted-foreground/60">
            Most engagements begin with a single constraint. Expansion is optional and diagnostic-led.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default EngagementShape;
